import { useEffect, useMemo, useState } from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend,
} from "recharts";
import api from "@/lib/api";
import { Sparkles, History, Info } from "lucide-react";

/**
 * SkillSnapshotRadar — DUPR-derived radar chart (iter52).
 *
 * Renders the player's per-axis DUPR level (2.5 → 5.5, snapped to 0.25). The
 * axis values are computed on the backend from the coach's assessment ticks,
 * so this component just fetches + draws.
 *
 * Rings are drawn every 0.25 (12 rings) and labelled every 0.5.
 * `showOverlay` toggle blends the previous month's polygon behind today's.
 */
export default function SkillSnapshotRadar({
  studentId,
  color = "#CCFF00",
  title = "Pickleball skill snapshot",
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOverlay, setShowOverlay] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/players/${studentId}/skill-snapshot`)
      .then((r) => { if (!cancelled) { setData(r.data); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || "Couldn't load snapshot"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  // Convert the backend `axes` map into recharts row data. Every ring is
  // relative to the [min, max] domain — we send the raw DUPR number and
  // let PolarRadiusAxis handle the scaling.
  const chartRows = useMemo(() => {
    if (!data) return [];
    const cur = data.current?.axes || {};
    const prev = data.previous?.axes || {};
    return (data.axes_order || []).map((axis) => ({
      skill: axis,
      current: cur[axis] == null ? null : Number(cur[axis]),
      previous: prev[axis] == null ? null : Number(prev[axis]),
    }));
  }, [data]);

  const ringMin = data?.ring_config?.min ?? 2.5;
  const ringMax = data?.ring_config?.max ?? 5.5;
  const ringStep = data?.ring_config?.step ?? 0.25;
  // Recharts renders one gridline per tick, but labelling every 0.25 clutters
  // the chart. We give it the labelled ticks only; the visual finer rings
  // come from the extra empty PolarGrid via `gridCount`.
  const ticks = data?.ring_config?.labelled || [2.5, 3.0, 3.5, 4.0, 4.5, 5.0, 5.5];
  const gridCount = Math.round((ringMax - ringMin) / ringStep); // 12 rings

  const weakest = data?.current?.weakest_axis;
  const hasPrevious = !!data?.previous;

  if (loading) {
    return (
      <div className="card-surface p-6 fade-up d2" data-testid="skill-snapshot-loading">
        <div className="label-mono mb-4">{title}</div>
        <div className={compact ? "h-48" : "h-72"} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-surface p-6 fade-up d2" data-testid="skill-snapshot-error">
        <div className="label-mono mb-2">{title}</div>
        <div className="text-xs text-[#FF4500]">{error}</div>
      </div>
    );
  }

  const noData = chartRows.every((r) => r.current == null);

  return (
    <div className="card-surface p-6 fade-up d2" data-testid="skill-snapshot-radar">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="label-mono flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-[#CCFF00]" />
          {title}
        </div>
        <div className="flex items-center gap-2">
          {hasPrevious ? (
            <button
              onClick={() => setShowOverlay((v) => !v)}
              className={`text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors inline-flex items-center gap-1 ${
                showOverlay
                  ? "border-[#CCFF00] text-[#CCFF00] bg-[#CCFF00]/10"
                  : "border-[#262626] text-[#A3A3A3] hover:border-[#525252]"
              }`}
              data-testid="skill-snapshot-overlay-toggle"
            >
              <History className="w-3 h-3" />
              {showOverlay ? "Hide" : "Show"} last month
            </button>
          ) : (
            <span
              className="text-[10px] uppercase tracking-wider text-[#525252] inline-flex items-center gap-1"
              title="No older snapshot on record yet — one is captured every time this chart loads."
            >
              <Info className="w-3 h-3" />
              No prior snapshot
            </span>
          )}
        </div>
      </div>

      {noData ? (
        <div className={`${compact ? "h-48" : "h-72"} flex items-center justify-center text-xs text-[#A3A3A3] leading-relaxed max-w-md mx-auto text-center px-4`} data-testid="skill-snapshot-empty">
          No ratings on the checklist yet. Once the coach ticks a few
          competencies, the polygon lights up here — each ring is a DUPR band
          from 2.5 (centre) to 5.5 (outer edge).
        </div>
      ) : (
        <div className={compact ? "h-56" : "h-80"}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartRows} outerRadius="80%">
              {/* Concentric grid — 12 rings for the 0.25 granularity. */}
              <PolarGrid stroke="#262626" gridType="polygon" radialLines gridCount={gridCount} />
              <PolarAngleAxis
                dataKey="skill"
                tick={{ fill: "#A3A3A3", fontSize: 10 }}
              />
              <PolarRadiusAxis
                domain={[ringMin, ringMax]}
                ticks={ticks}
                tick={false}
                axisLine={false}
                stroke="transparent"
              />
              {/* Previous polygon — faded, only rendered when overlay is toggled on. */}
              {showOverlay && hasPrevious && (
                <Radar
                  name="Last month"
                  dataKey="previous"
                  stroke="#A3A3A3"
                  strokeDasharray="4 4"
                  fill="#A3A3A3"
                  fillOpacity={0.08}
                  isAnimationActive={false}
                  connectNulls={false}
                />
              )}
              <Radar
                name="Today"
                dataKey="current"
                stroke={color}
                fill={color}
                fillOpacity={0.28}
                isAnimationActive={true}
                connectNulls={false}
              />
              {showOverlay && hasPrevious && (
                <Legend
                  align="right"
                  verticalAlign="bottom"
                  wrapperStyle={{ fontSize: 10, color: "#A3A3A3" }}
                />
              )}
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Weakest axis callout — the whole point of the radar */}
      {weakest && !noData && (
        <div
          className="mt-3 text-[11px] flex items-start gap-2 border-t border-[#141414] pt-3"
          data-testid="skill-snapshot-weakest"
        >
          <span className="uppercase tracking-wider text-[#525252] mt-0.5">Next focus</span>
          <span className="text-[#E5E5E5]">
            <strong style={{ color }}>{weakest}</strong>
            {" — the shortest arm on the chart. Priority for the next session."}
          </span>
        </div>
      )}

      {/* Ring legend line */}
      {!noData && (
        <div className="mt-2 text-[10px] text-[#525252]">
          Rings = DUPR levels · centre {ringMin.toFixed(1)} → outer {ringMax.toFixed(1)} · 0.25 increments · derived from your assessment ticks
        </div>
      )}
    </div>
  );
}
